import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, IconButton, Collapse,
  Paper, Tabs, Tab,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import PersonIcon from "@mui/icons-material/Person";
import { axiosPartners } from "../../axios/axiosConfig";
import toast from "react-hot-toast";

const fmt = (dateStr) =>
  dateStr
    ? new Date(dateStr).toLocaleString("en-IN", {
        day: "2-digit", month: "short", year: "2-digit",
        hour: "2-digit", minute: "2-digit",
      })
    : "—";

const statusColor = (status) => {
  const s = status?.toUpperCase();
  if (s === "COMPLETE" || s === "COMPLETED") return { bg: "#dcfce7", color: "#16a34a" };
  if (s === "CANCEL" || s === "CANCELLED")   return { bg: "#fee2e2", color: "#dc2626" };
  if (s === "PENDING")                        return { bg: "#fef9c3", color: "#b45309" };
  if (s === "OPEN")                           return { bg: "#dbeafe", color: "#2563eb" };
  if (s === "INACTIVE")                       return { bg: "#f1f5f9", color: "#64748b" };
  return { bg: "#f1f5f9", color: "#64748b" };
};

const signalColor = (signal) =>
  signal?.toLowerCase() === "buy"
    ? { bg: "#dcfce7", color: "#16a34a" }
    : { bg: "#fee2e2", color: "#dc2626" };

const STATUSES = ["ALL", "CANCEL", "PENDING", "COMPLETED", "INACTIVE"];

// ─── Orders sub-table ────────────────────────────────────────────────────────
const OrdersRow = ({ orders }) => (
  <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden", mt: 0.5 }}>
    <Table size="small">
      <TableHead>
        <TableRow sx={{ background: "#f8fafc" }}>
          {["Order ID", "Status", "Qty", "Type", "Product", "Order Type", "Segment", "Hit Count", "Exiting"].map((h) => (
            <TableCell key={h} sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", py: 1, whiteSpace: "nowrap" }}>{h}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {orders.length === 0 ? (
          <TableRow><TableCell colSpan={9} sx={{ textAlign: "center", py: 2, fontSize: 12, color: "#94a3b8" }}>No orders</TableCell></TableRow>
        ) : orders.map((o) => {
          const sc = statusColor(o.order_status);
          return (
            <TableRow key={o.order_id} sx={{ "&:hover": { background: "#f8fafc" } }}>
              <TableCell sx={{ fontSize: 12, color: "#64748b" }}>{o.order_id}</TableCell>
              <TableCell><Chip label={o.order_status} size="small" sx={{ background: sc.bg, color: sc.color, fontWeight: 700, fontSize: 10, height: 20, borderRadius: "4px" }} /></TableCell>
              <TableCell sx={{ fontSize: 12, color: "#374151" }}>{o.quantity}</TableCell>
              <TableCell sx={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>{o.transaction_type}</TableCell>
              <TableCell sx={{ fontSize: 12, color: "#374151" }}>{o.product_code}</TableCell>
              <TableCell sx={{ fontSize: 12, color: "#374151" }}>{o.order_type}</TableCell>
              <TableCell sx={{ fontSize: 12, color: "#374151" }}>{o.exchange_segment}</TableCell>
              <TableCell sx={{ fontSize: 12, color: "#374151" }}>{o.execution_hit_count}</TableCell>
              <TableCell sx={{ fontSize: 12, color: o.exiting_trade ? "#dc2626" : "#94a3b8" }}>{o.exiting_trade ? "Yes" : "No"}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </TableContainer>
);

// ─── Trade row ────────────────────────────────────────────────────────────────
const TradeRow = ({ trade, index }) => {
  const [expanded, setExpanded] = useState(false);
  const sc = statusColor(trade.order_status);
  const sg = signalColor(trade.entry_signal);

  return (
    <>
      <TableRow sx={{ "&:hover": { background: "#f8fafc" }, transition: "background 0.15s" }}>
        <TableCell sx={{ fontSize: 12, color: "#94a3b8", py: 1.5 }}>{index + 1}</TableCell>

        <TableCell sx={{ py: 1.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 13, color: "#04343a", lineHeight: 1.3 }}>{trade.symbol_name}</Typography>
          <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>{trade.trading_symbol}</Typography>
        </TableCell>

        <TableCell sx={{ fontSize: 12, color: "#374151", py: 1.5 }}>{trade.broker_name}</TableCell>
        <TableCell sx={{ fontSize: 12, color: "#374151", py: 1.5, fontFamily: "monospace" }}>{trade.client_code}</TableCell>

        <TableCell sx={{ py: 1.5 }}>
          <Chip label={trade.entry_signal} size="small" sx={{ background: sg.bg, color: sg.color, fontWeight: 700, fontSize: 11, height: 22, borderRadius: "6px" }} />
        </TableCell>

        <TableCell sx={{ py: 1.5 }}>
          <Chip label={trade.order_status} size="small" sx={{ background: sc.bg, color: sc.color, fontWeight: 700, fontSize: 11, height: 22, borderRadius: "6px" }} />
        </TableCell>

        <TableCell sx={{ fontSize: 12, color: "#374151", py: 1.5 }}>
          {trade.green_quantity} @ {trade.green_price ?? "—"}
        </TableCell>

        <TableCell sx={{ fontSize: 12, color: "#374151", py: 1.5 }}>
          {trade.stoploss > 0 ? `₹${trade.stoploss}` : "—"}
        </TableCell>

        <TableCell sx={{ fontSize: 12, color: "#374151", py: 1.5, whiteSpace: "nowrap" }}>
          <Typography sx={{ fontSize: 11 }}><span style={{ color: "#16a34a", fontWeight: 600 }}>H</span> {trade.day_high ?? "—"}</Typography>
          <Typography sx={{ fontSize: 11 }}><span style={{ color: "#dc2626", fontWeight: 600 }}>L</span> {trade.day_low ?? "—"}</Typography>
        </TableCell>

        <TableCell sx={{ py: 1.5 }}>
          <Box sx={{ display: "flex", gap: 0.4, flexWrap: "wrap" }}>
            {[
              { label: "G", value: trade.set_green_alert_consent, title: "Green Alert" },
              { label: "R", value: trade.set_red_alert_consent,   title: "Red Alert" },
              { label: "SL", value: trade.stoploss_exit_consent,  title: "SL Exit" },
            ].map((c) => (
              <Box key={c.label} title={c.title} sx={{ fontSize: 9, fontWeight: 700, px: 0.6, py: 0.2, borderRadius: "4px", background: c.value ? "#dcfce7" : "#fee2e2", color: c.value ? "#16a34a" : "#dc2626", lineHeight: 1.5 }}>
                {c.label}
              </Box>
            ))}
          </Box>
        </TableCell>

        <TableCell sx={{ fontSize: 12, color: "#94a3b8", py: 1.5, whiteSpace: "nowrap" }}>{fmt(trade.created_at)}</TableCell>

        <TableCell sx={{ py: 1.5 }}>
          <IconButton
            size="small"
            onClick={() => setExpanded((p) => !p)}
            sx={{ color: "#016551", "&:hover": { background: "#f0fdf4" } }}
          >
            {expanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </IconButton>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={13} sx={{ py: 0, border: 0 }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ px: 3, py: 1.5, background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", mb: 1, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Orders ({trade.orders?.length ?? 0})
              </Typography>
              <OrdersRow orders={trade.orders ?? []} />

              {trade.stoploss_history?.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", mb: 1, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    Stoploss Trail ({trade.stoploss_history.length})
                  </Typography>
                  <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ background: "#f8fafc" }}>
                          {["#", "Prev Value", "Current Price", "New Value", "By", "At"].map((h) => (
                            <TableCell key={h} sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", py: 1, whiteSpace: "nowrap" }}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {trade.stoploss_history.map((sl, idx) => (
                          <TableRow key={sl.id ?? idx} sx={{ "&:hover": { background: "#f8fafc" } }}>
                            <TableCell sx={{ fontSize: 12, color: "#94a3b8" }}>{idx + 1}</TableCell>
                            <TableCell sx={{ fontSize: 12, color: "#64748b" }}>{sl.previous_value != null ? `₹${sl.previous_value}` : "—"}</TableCell>
                            <TableCell sx={{ fontSize: 12, fontWeight: 600, color: "#016551" }}>{sl.current_price != null ? `₹${sl.current_price}` : "—"}</TableCell>
                            <TableCell sx={{ fontSize: 12, fontWeight: 700, color: "#dc2626" }}>{sl.new_value != null ? `₹${sl.new_value}` : "—"}</TableCell>
                            <TableCell sx={{ fontSize: 11, color: "#64748b" }}>{sl.created_by ?? "—"}</TableCell>
                            <TableCell sx={{ fontSize: 11, color: "#94a3b8", whiteSpace: "nowrap" }}>{fmt(sl.created_at)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const CustomerTradeInfo = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [trades, setTrades]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [activeTab, setActiveTab] = useState(0);

  const userInfo = trades[0]?.user_information?.[0]?.[0];

  const fetchTrades = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosPartners.get(`/brokers/trade-dashboard/?client_id=${clientId}`);
      setTrades(res.data?.data ?? res.data ?? []);
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || "Failed to load trades");
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { fetchTrades(); }, [fetchTrades]);

  const filteredTrades = useMemo(() => {
    const status = STATUSES[activeTab];
    if (status === "ALL") return trades;
    return trades.filter((t) => t.order_status?.toUpperCase() === status);
  }, [trades, activeTab]);

  const countFor = (status) =>
    status === "ALL" ? trades.length : trades.filter((t) => t.order_status?.toUpperCase() === status).length;

  const buyCount  = filteredTrades.filter((t) => t.entry_signal?.toLowerCase() === "buy").length;
  const sellCount = filteredTrades.length - buyCount;

  return (
    <Box sx={{ p: 3, background: "#eef2f7", minHeight: "100vh" }}>

      {/* Header */}
      <Box sx={{ background: "#04343a", borderRadius: "12px", px: 3, py: 2, mb: 3, display: "flex", alignItems: "center", justifyContent: "space-between", boxShadow: "0 4px 20px rgba(4,52,58,0.3)" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <IconButton onClick={() => navigate(-1)} size="small" sx={{ color: "#eef2f7", "&:hover": { background: "rgba(255,255,255,0.1)" }, mr: 0.5 }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <PersonIcon sx={{ color: "#eef2f7", fontSize: 26 }} />
          <Box>
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 18, lineHeight: 1.2 }}>
              {loading ? "Loading…" : userInfo?.username || `Client #${clientId}`}
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: 12 }}>
              {userInfo?.phoneno && `${userInfo.phoneno} · `}{userInfo?.email || `client_id: ${clientId}`}
            </Typography>
          </Box>
        </Box>
        {!loading && userInfo && (
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            {[
              { label: "User Type", value: userInfo.user_type },
              { label: "Joined",    value: userInfo.date_joined },
            ].map((d) => (
              <Box key={d.label} sx={{ textAlign: "right" }}>
                <Typography sx={{ fontSize: 10, color: "rgba(255,255,255,0.5)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{d.label}</Typography>
                <Typography sx={{ fontSize: 13, color: "#fff", fontWeight: 600 }}>{d.value || "—"}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Summary cards */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        {[
          { label: "Total Trades", value: trades.length,          color: "#04343a" },
          { label: "Buy",          value: buyCount,               color: "#16a34a" },
          { label: "Sell",         value: sellCount,              color: "#dc2626" },
          { label: "Showing",      value: filteredTrades.length,  color: "#016551" },
        ].map((c) => (
          <Box key={c.label} sx={{ background: "#fff", borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 1px 6px rgba(0,0,0,0.07)", px: 3, py: 2, minWidth: 130, flex: 1 }}>
            <Typography sx={{ fontSize: 12, color: "#94a3b8", fontWeight: 500, mb: 0.5 }}>{c.label}</Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 800, color: c.color }}>{c.value}</Typography>
          </Box>
        ))}
      </Box>

      {/* Status Tabs */}
      <Box sx={{ background: "#fff", borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 1px 6px rgba(0,0,0,0.07)", mb: 3, overflow: "hidden" }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            background: "#04343a", minHeight: 44,
            "& .MuiTab-root": { color: "rgba(255,255,255,0.55)", fontWeight: 600, fontSize: 12, minHeight: 44, textTransform: "none", px: 2.5 },
            "& .Mui-selected": { color: "#fff !important" },
            "& .MuiTabs-indicator": { backgroundColor: "#1db954", height: 3 },
            "& .MuiTabScrollButton-root": { color: "rgba(255,255,255,0.6)" },
          }}
        >
          {STATUSES.map((s, i) => (
            <Tab key={s} label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                {s}
                <Box sx={{ background: activeTab === i ? "#1db954" : "rgba(255,255,255,0.15)", color: "#fff", borderRadius: "10px", px: 0.8, py: 0.1, fontSize: 10, fontWeight: 700, lineHeight: 1.6, minWidth: 20, textAlign: "center" }}>
                  {countFor(s)}
                </Box>
              </Box>
            } />
          ))}
        </Tabs>
      </Box>

      {/* Table */}
      <Box sx={{ background: "#fff", borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 1px 6px rgba(0,0,0,0.07)", overflow: "hidden" }}>
        <Box sx={{ background: "#04343a", px: 3, py: 1.5 }}>
          <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: 14 }}>Trade History</Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress sx={{ color: "#016551" }} size={36} />
          </Box>
        ) : filteredTrades.length === 0 ? (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <Typography sx={{ color: "#94a3b8", fontSize: 14 }}>No trades for this status</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: "#f8fafc" }}>
                  {["#", "Symbol", "Broker", "Client Code", "Signal", "Status", "Qty @ Price", "Stoploss", "Day H/L", "Consents", "Created At", "Orders"].map((h) => (
                    <TableCell key={h} sx={{ fontSize: 12, fontWeight: 700, color: "#64748b", py: 1.5, borderBottom: "2px solid #e2e8f0", whiteSpace: "nowrap" }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTrades.map((trade, i) => (
                  <TradeRow key={trade.trade_id} trade={trade} index={i} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
};

export default CustomerTradeInfo;
